import { isEmpty } from 'lodash';

/**
 * Takes the backend properties and normalizes them.
 *
 * @param {Book} book - a Book
 * @param {Object} properties - a JSON object of the record from the backend
 * @returns {Object} a JSON object with normalized values
 */
const normalizeUserContentProperties = (book, properties) => {
  // All user content must have a document index.
  const documentIndex = Math.min(
    Number(properties.documentIndex ?? properties.spineEntry?.playOrder ?? 0),
    book.documents.length - 1,
  );

  const pageIndex =
    properties.pageIndex ??
    (properties.epubPage ? properties.epubPage?.index - 1 : undefined);
  const markerIndex =
    properties.markerIndex ??
    (properties.ncxEntry ? properties.ncxEntry.index - 1 : undefined);

  const documentIndices =
    properties.documentIndices ?? properties.documentIndicies ?? [];
  const markerIndices =
    properties.markerIndices ?? properties.markerIndicies ?? [];

  // Location properties
  const page = book.pages[pageIndex] ?? null;
  const document = book.documents[documentIndex] ?? null;
  const cfi = properties.cfi;

  // Marker properties with fallback to page's marker when not directly assigned
  const marker = book.markers[markerIndex] ?? page?.marker ?? null;
  const topLevelMarker =
    book.markers[markerIndex]?.topLevelMarker ??
    page?.marker?.topLevelMarker ??
    null;

  // For flashcards: use the deck id as the deckKey for each flashcard
  const deckKey = properties.flashcardDeck?.id;

  // Normalize anything representing clipped text from the book content as "snippet"
  const snippet = properties.snippet ?? properties.text ?? properties.term;
  // Normalize anything representing user inputted text as "userText"
  const userText =
    properties.comment ?? properties.title ?? properties.description;

  // Normalize user ID
  const userId = properties.owner?.id ?? properties.user?.id;
  const firstName = properties.owner?.firstName ?? null;
  const lastName = properties.owner?.lastName ?? null;

  const normalizedProperties = {
    book,
    cfi,
    deckKey,
    document,
    documentIndices,
    firstName,
    lastName,
    marker,
    markerIndices,
    page,
    snippet,
    topLevelMarker,
    userId,
    userText,
    ...properties,
  };

  // Any property that is supposed to be an object, but came in as a string.
  for (let property of [
    'documentIndices',
    'markerIndices',
    'documentResources',
  ]) {
    if (typeof normalizedProperties[property] === 'string') {
      normalizedProperties[property] = JSON.parse(
        normalizedProperties[property],
      );
    }
  }

  if (
    !isEmpty(normalizedProperties.documentResources) &&
    !normalizedProperties.markerIndices?.length
  ) {
    normalizedProperties.wholeBook = true;
  }

  // Remove the database-named properties. They are just clutter now.
  delete normalizedProperties.flashcardDeck;
  delete normalizedProperties.text; // the database representation of snippet for copied texts
  delete normalizedProperties.term; // the database representation of snippet for flashcards
  delete normalizedProperties.description; // the database representation of userText for flashcards
  delete normalizedProperties.title; // the database representation of userText for flashcard decks
  delete normalizedProperties.comment; // the database representation of userText for notes
  delete normalizedProperties.playOrders; // alias of documentIndices
  delete normalizedProperties.documentIndicies; // alias of documentIndices
  delete normalizedProperties.markerIndicies; // alias of markerIndices

  return normalizedProperties;
};

export default normalizeUserContentProperties;
